/**
 * SMS Grup Portal - Burs / Aile Yardımı kurumsal Excel dışa aktarımı
 * Kurulum: npm install exceljs
 * Bu dosyayı Student.jsx ve Family.jsx ile aynı klasöre yerleştirin.
 * Tamamen tarayıcıda çalışır; indirme için yeni bir backend API çağrısı yapmaz.
 */
import ExcelJS from 'exceljs';

const COLORS = {
  navy: '14243B',
  navy2: '213A5B',
  white: 'FFFFFF',
  ink: '20344D',
  muted: '61748A',
  line: 'E5EBF2',
  zebra: 'F5F8FC',
  pale: 'EAF0F7',
  green: '087F5B',
  paleGreen: 'E1F5EB',
  orange: 'A65F00',
  paleOrange: 'FFF1D6',
  red: 'B4233A',
  paleRed: 'FCE8ED',
  indigo: '345CD1',
  paleIndigo: 'E9EFFF',
};

const argb = (value) => value.length === 6 ? `FF${value}` : value;

const FORMAT_MONEY = '#,##0.00 "₺"';
const FORMAT_DATE = 'dd.mm.yyyy';

const DATA_COLUMNS = {
  student: [
    { header: 'Sıra', key: 'index', width: 8, type: 'number' },
    { header: 'Öğrenci No', key: 'studentNo', width: 17, type: 'text' },
    { header: 'T.C. Kimlik No', key: 'tcNo', width: 20, type: 'text' },
    { header: 'Ad Soyad', key: 'name', width: 28, type: 'text' },
    { header: 'Telefon', key: 'phone', width: 19, type: 'text' },
    { header: 'Referans', key: 'reference', width: 25, type: 'text' },
    { header: 'Okul / Üniversite', key: 'school', width: 35, type: 'text' },
    { header: 'Bölüm', key: 'department', width: 29, type: 'text' },
    { header: 'Sınıf', key: 'grade', width: 15, type: 'text' },
    { header: 'Toplam Öğrenim Yılı', key: 'totalEducationYears', width: 22, type: 'int' },
    { header: 'Aylık Burs (TL)', key: 'amount', width: 21, type: 'money' },
    { header: 'Başlangıç Tarihi', key: 'startDate', width: 19, type: 'date' },
    { header: 'Durum', key: 'status', width: 16, type: 'status' },
    { header: 'Banka', key: 'bank', width: 24, type: 'text' },
    { header: 'IBAN', key: 'iban', width: 34, type: 'text' },
    { header: 'Adres', key: 'address', width: 44, type: 'text' },
    { header: 'Açıklama', key: 'explanation', width: 47, type: 'text' },
  ],
  family: [
    { header: 'Sıra', key: 'index', width: 8, type: 'number' },
    { header: 'T.C. Kimlik No', key: 'tcNo', width: 20, type: 'text' },
    { header: 'Aile / Temsilci Adı', key: 'name', width: 30, type: 'text' },
    { header: 'Telefon', key: 'phone', width: 19, type: 'text' },
    { header: 'Adres', key: 'address', width: 45, type: 'text' },
    { header: 'Açıklama', key: 'explanation', width: 47, type: 'text' },
    { header: 'Aylık Yardım (TL)', key: 'amount', width: 23, type: 'money' },
    { header: 'Banka', key: 'bank', width: 24, type: 'text' },
    { header: 'IBAN', key: 'iban', width: 34, type: 'text' },
    { header: 'Referans', key: 'reference', width: 25, type: 'text' },
    { header: 'Durum', key: 'status', width: 16, type: 'status' },
    { header: 'Başlangıç Tarihi', key: 'startDate', width: 19, type: 'date' },
  ],
};

const CONFIG = {
  student: {
    title: 'ÖĞRENCİ BURS RAPORU',
    subtitle: 'Bursiyer takip sistemi • Yönetim raporu',
    detailSheet: 'Bursiyer Listesi',
    accent: '2563EB',
    paleAccent: 'EAF1FF',
    label: 'Öğrenci',
    amountLabel: 'Aylık aktif burs',
    categoryLabel: 'Okul / Üniversite',
    categoryField: 'school',
    filename: 'bursiyer_raporu',
    statuses: ['Aktif', 'Pasif', 'Beklemede', 'Mezun'],
  },
  family: {
    title: 'AİLE SOSYAL YARDIM RAPORU',
    subtitle: 'Sosyal yardım takip sistemi • Yönetim raporu',
    detailSheet: 'Aile Yardım Listesi',
    accent: '059669',
    paleAccent: 'E5F7EE',
    label: 'Hane',
    amountLabel: 'Aylık aktif yardım',
    categoryLabel: 'Banka',
    categoryField: 'bank',
    filename: 'aile_yardimlari_raporu',
    statuses: ['Aktif', 'Beklemede', 'Pasif'],
  },
};

function textValue(value) {
  if (value === null || value === undefined) return '';
  const raw = String(value);
  // Hücreye girilen metinler formül olarak yorumlanmasın.
  return /^[=+\-@\t\r]/.test(raw) ? `'${raw}` : raw;
}

function moneyValue(value) {
  const result = Number(value);
  return Number.isFinite(result) ? result : 0;
}

function intValue(value) {
  if (value === null || value === undefined || value === '') return null;
  const result = Number(value);
  return Number.isFinite(result) ? result : textValue(value);
}

function dateValue(value) {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value === 'string') {
    const match = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (match) {
      const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
      if (!Number.isNaN(date.getTime())) return date;
    }
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function dateLabel(value) {
  const date = dateValue(value);
  return date ? date.toLocaleDateString('tr-TR') : 'Tümü';
}

function statusColors(value, config) {
  const status = String(value || '').toLocaleLowerCase('tr-TR');
  if (status === 'aktif') return [COLORS.green, COLORS.paleGreen];
  if (status === 'beklemede') return [COLORS.orange, COLORS.paleOrange];
  if (status === 'mezun') return [config.accent, config.paleAccent];
  if (status === 'pasif') return [COLORS.red, COLORS.paleRed];
  return [COLORS.muted, COLORS.pale];
}

function merged(ws, fromRow, fromCol, toRow, toCol, value, opts = {}) {
  ws.mergeCells(fromRow, fromCol, toRow, toCol);
  const cell = ws.getCell(fromRow, fromCol);
  cell.value = value;
  cell.font = { name: 'Aptos', size: opts.size || 11, bold: !!opts.bold, color: { argb: argb(opts.color || COLORS.ink) } };
  cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: argb(opts.fill || COLORS.white) } };
  cell.alignment = {
    vertical: 'middle',
    horizontal: opts.align || 'left',
    wrapText: !!opts.wrap,
    indent: opts.indent || 0,
  };
  if (opts.format) cell.numFmt = opts.format;
  return cell;
}

function drawMetric(ws, startCol, endCol, label, value, opts = {}) {
  merged(ws, 6, startCol, 6, endCol, `  ${label.toLocaleUpperCase('tr-TR')}`, {
    size: 10,
    bold: true,
    color: COLORS.muted,
    fill: opts.fill || COLORS.pale,
  });
  merged(ws, 7, startCol, 8, endCol, value, {
    size: 19,
    bold: true,
    color: opts.color || COLORS.navy,
    fill: opts.fill || COLORS.pale,
    indent: 1,
    format: opts.format,
  });
}

function sectionBar(ws, row, text, fill, maxCol = 12) {
  merged(ws, row, 1, row, maxCol, `  ${text}`, {
    fill,
    color: COLORS.white,
    bold: true,
    size: 11,
  });
  ws.getRow(row).height = 27;
}

function dataSheet(workbook, cfg, kind, rows, summary, filterDescription, generatedAt) {
  const definitions = DATA_COLUMNS[kind];
  const totalCols = definitions.length;
  const ws = workbook.addWorksheet(cfg.detailSheet, {
    views: [{ state: 'frozen', ySplit: 10, activeCell: 'D11' }],
    properties: { defaultRowHeight: 21 },
    pageSetup: {
      paperSize: 8, // A3
      orientation: 'landscape',
      fitToPage: true,
      fitToWidth: 1,
      fitToHeight: 0,
      margins: { left: 0.25, right: 0.25, top: 0.45, bottom: 0.45, header: 0.2, footer: 0.2 },
    },
  });

  definitions.forEach((definition, i) => {
    ws.getColumn(i + 1).width = definition.width;
  });
    ws.headerFooter.oddFooter = '&LSMS Grup Portal  |  Kurum İçi / Kişisel Veri&R Sayfa &P / &N';
  ws.pageSetup.printTitlesRow = '10:10';

  merged(ws, 1, 1, 2, totalCols, `  ${cfg.title}`, {
    fill: COLORS.navy,
    color: COLORS.white,
    size: 20,
    bold: true,
  });
  ws.getRow(1).height = 29;
  ws.getRow(2).height = 27;
  merged(ws, 3, 1, 3, totalCols, `  SMS GRUP PORTAL   •   ${cfg.subtitle}   •   ${generatedAt}`, {
    fill: cfg.accent,
    color: COLORS.white,
    bold: true,
    size: 11,
  });
  ws.getRow(3).height = 28;
  merged(ws, 4, 1, 4, totalCols, `  Aktif filtre: ${filterDescription}`, {
    fill: cfg.paleAccent,
    color: COLORS.ink,
    size: 10,
  });
  ws.getRow(4).height = 25;
  ws.getRow(5).height = 10;

  const third = Math.floor(totalCols / 3);
  drawMetric(ws, 1, third, `Toplam ${cfg.label}`, summary.total, { fill: COLORS.pale });
  drawMetric(ws, third + 1, 2 * third, 'Aktif Kayıt', summary.active, { fill: COLORS.paleGreen, color: COLORS.green });
  drawMetric(ws, 2 * third + 1, totalCols, cfg.amountLabel, summary.activeAmount, {
    fill: cfg.paleAccent,
    color: cfg.accent,
    format: FORMAT_MONEY,
  });
  ws.getRow(6).height = 25;
  ws.getRow(7).height = 27;
  ws.getRow(8).height = 23;
  merged(ws, 9, 1, 9, totalCols, '  Filtrelenmiş ve sıralanmış tüm kayıtlar listelenir. Para alanları hesaplamaya uygundur. Kimlik / IBAN alanları metin biçimindedir.', {
    fill: COLORS.pale,
    color: COLORS.muted,
    size: 10,
  });
  ws.getRow(9).height = 23;

  const headRow = ws.getRow(10);
  headRow.height = 35;
  definitions.forEach((def, colIndex) => {
    const cell = headRow.getCell(colIndex + 1);
    cell.value = def.header;
    cell.font = { name: 'Aptos', size: 10, bold: true, color: { argb: argb(COLORS.white) } };
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: argb(COLORS.navy2) } };
    cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
    cell.border = { bottom: { style: 'medium', color: { argb: argb(cfg.accent) } } };
  });

  rows.forEach((record, index) => {
    const rowNum = index + 11;
    const row = ws.getRow(rowNum);
    row.height = 25;
    const alternate = index % 2 === 0 ? COLORS.white : COLORS.zebra;
    definitions.forEach((def, i) => {
      const cell = row.getCell(i + 1);
      let value;
      if (def.key === 'index') value = index + 1;
      else if (def.type === 'money') value = moneyValue(record?.[def.key]);
      else if (def.type === 'int') value = intValue(record?.[def.key]);
      else if (def.type === 'date') value = dateValue(record?.[def.key]);
      else value = textValue(record?.[def.key]);

      cell.value = value;
      cell.font = { name: 'Aptos', size: 10, color: { argb: argb(COLORS.ink) }, bold: def.key === 'name' };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: argb(alternate) } };
      cell.alignment = {
        vertical: 'middle',
        horizontal: def.type === 'money' || def.type === 'int' || def.type === 'number' ? 'right' : 'left',
      };
      cell.border = { bottom: { style: 'hair', color: { argb: argb(COLORS.line) } } };

      if (def.type === 'money') {
        cell.numFmt = FORMAT_MONEY;
        cell.font = { name: 'Aptos', size: 10, bold: true, color: { argb: argb(cfg.accent) } };
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: argb(cfg.paleAccent) } };
      } else if (def.type === 'date' && value) cell.numFmt = FORMAT_DATE;
      else if (def.type === 'text' || def.type === 'status') cell.numFmt = '@';

      if (def.type === 'status') {
        const [textColor, fillColor] = statusColors(value, cfg);
        cell.font = { name: 'Aptos', size: 10, bold: true, color: { argb: argb(textColor) } };
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: argb(fillColor) } };
        cell.alignment = { vertical: 'middle', horizontal: 'center' };
      }
    });
  });

  if (rows.length === 0) {
    merged(ws, 11, 1, 11, totalCols, '  Seçilen filtreye uygun kayıt bulunamadı.', {
      color: COLORS.muted,
      fill: COLORS.zebra,
    });
  }

  ws.autoFilter = { from: { row: 10, column: 1 }, to: { row: Math.max(11, 10 + rows.length), column: totalCols } };
  ws.pageSetup.printArea = `A1:${ws.getColumn(totalCols).letter}${Math.max(11, 10 + rows.length)}`;
  return ws;
}

function summarySheet(workbook, cfg, kind, rows, metrics, filterDescription, generatedAt) {
  const ws = workbook.addWorksheet('Yönetici Özeti', {
    views: [{ state: 'frozen', ySplit: 3 }],
    properties: { defaultRowHeight: 23 },
    pageSetup: {
      paperSize: 9, // A4
      orientation: 'landscape',
      fitToPage: true,
      fitToWidth: 1,
      fitToHeight: 1,
      margins: { left: 0.4, right: 0.4, top: 0.55, bottom: 0.55, header: 0.2, footer: 0.2 },
    },
  });
  for (let col = 1; col <= 12; col += 1) ws.getColumn(col).width = 13.5;
  ws.headerFooter.oddFooter = '&LSMS Grup Portal  |  Kurum İçi / Kişisel Veri&R Sayfa &P / &N';

  merged(ws, 1, 1, 2, 12, `  ${cfg.title}  |  YÖNETİCİ ÖZETİ`, {
    fill: COLORS.navy, color: COLORS.white, bold: true, size: 18,
  });
  ws.getRow(1).height = 30;
  ws.getRow(2).height = 25;
  merged(ws, 3, 1, 3, 12, `  SMS GRUP PORTAL     •     Oluşturma: ${generatedAt}     •     ${cfg.subtitle}`, {
    fill: cfg.accent, color: COLORS.white, bold: true, size: 10,
  });
  ws.getRow(3).height = 28;
  merged(ws, 4, 1, 4, 12, `  Uygulanan filtreler: ${filterDescription}`, {
    fill: cfg.paleAccent, color: COLORS.ink, size: 10,
  });
  ws.getRow(4).height = 26;
  ws.getRow(5).height = 9;

  drawMetric(ws, 1, 3, `Toplam ${cfg.label}`, metrics.total, { fill: COLORS.pale });
  drawMetric(ws, 4, 6, 'Aktif kayıt', metrics.active, { fill: COLORS.paleGreen, color: COLORS.green });
  drawMetric(ws, 7, 9, cfg.amountLabel, metrics.activeAmount, {
    fill: cfg.paleAccent, color: cfg.accent, format: FORMAT_MONEY,
  });
  drawMetric(ws, 10, 12, 'Tüm kayıtların toplamı', metrics.allAmount, {
    fill: COLORS.pale, color: COLORS.navy, format: FORMAT_MONEY,
  });
  ws.getRow(6).height = 26;
  ws.getRow(7).height = 29;
  ws.getRow(8).height = 26;
  ws.getRow(9).height = 12;

  sectionBar(ws, 10, 'DURUM BAZLI DAĞILIM', COLORS.navy2);
  merged(ws, 11, 1, 11, 4, '  DURUM', { fill: COLORS.pale, bold: true, color: COLORS.muted });
  merged(ws, 11, 5, 11, 6, 'KAYIT', { fill: COLORS.pale, bold: true, align: 'center', color: COLORS.muted });
  merged(ws, 11, 7, 11, 9, 'AYLIK TOPLAM', { fill: COLORS.pale, bold: true, align: 'right', color: COLORS.muted });
  merged(ws, 11, 10, 11, 12, 'ORAN', { fill: COLORS.pale, bold: true, align: 'center', color: COLORS.muted });
  ws.getRow(11).height = 26;

  const knownStatuses = cfg.statuses;
  const hasOther = rows.some((row) => !knownStatuses.includes(row?.status));
  const statusList = hasOther ? [...knownStatuses, 'Diğer'] : [...knownStatuses];
  statusList.forEach((status, index) => {
    const rowIndex = 12 + index;
    const matching = rows.filter((row) => status === 'Diğer' ? !knownStatuses.includes(row?.status) : row?.status === status);
    const count = matching.length;
    const amount = matching.reduce((sum, row) => sum + moneyValue(row?.amount), 0);
    const [textColor, fillColor] = statusColors(status, cfg);
    const base = index % 2 === 0 ? COLORS.white : COLORS.zebra;
    merged(ws, rowIndex, 1, rowIndex, 4, `  ${status}`, { fill: fillColor, color: textColor, bold: true });
    merged(ws, rowIndex, 5, rowIndex, 6, count, { fill: base, align: 'center', bold: true });
    merged(ws, rowIndex, 7, rowIndex, 9, amount, { fill: base, align: 'right', format: FORMAT_MONEY, bold: true });
    const pct = metrics.total ? (100 * count / metrics.total) : 0;
    const visual = count ? `▰`.repeat(Math.max(1, Math.round(pct / 10))) : '—';
    merged(ws, rowIndex, 10, rowIndex, 12, `${visual}  %${pct.toFixed(1)}`, {
      fill: base, color: textColor, align: 'center', bold: true, size: 10,
    });
    ws.getRow(rowIndex).height = 28;
  });

  const catStart = 19;
  sectionBar(ws, catStart, `EN ÇOK KAYIT BULUNAN ${cfg.categoryLabel.toLocaleUpperCase('tr-TR')} ALANLARI`, COLORS.navy2);
  merged(ws, catStart + 1, 1, catStart + 1, 7, `  ${cfg.categoryLabel.toLocaleUpperCase('tr-TR')}`, { fill: COLORS.pale, bold: true, color: COLORS.muted });
  merged(ws, catStart + 1, 8, catStart + 1, 9, 'KAYIT', { fill: COLORS.pale, bold: true, align: 'center', color: COLORS.muted });
  merged(ws, catStart + 1, 10, catStart + 1, 12, 'AYLIK TOPLAM', { fill: COLORS.pale, bold: true, align: 'right', color: COLORS.muted });

  const categories = new Map();
  rows.forEach((row) => {
    const key = String(row?.[cfg.categoryField] || 'Belirtilmemiş').trim() || 'Belirtilmemiş';
    const entry = categories.get(key) || { name: key, count: 0, amount: 0 };
    entry.count += 1;
    entry.amount += moneyValue(row?.amount);
    categories.set(key, entry);
  });
  const ranked = [...categories.values()].sort((a, b) => b.count - a.count || b.amount - a.amount).slice(0, 6);
  (ranked.length ? ranked : [{ name: 'Kayıt bulunamadı', count: 0, amount: 0 }]).forEach((item, index) => {
    const r = catStart + 2 + index;
    const fill = index % 2 === 0 ? COLORS.white : COLORS.zebra;
    merged(ws, r, 1, r, 7, `  ${textValue(item.name)}`, { fill, color: COLORS.ink, bold: index === 0 });
    merged(ws, r, 8, r, 9, item.count, { fill, align: 'center', bold: true });
    merged(ws, r, 10, r, 12, item.amount, { fill, align: 'right', format: FORMAT_MONEY, color: cfg.accent });
    ws.getRow(r).height = 27;
  });
  merged(ws, 30, 1, 31, 12,
    '  BİLGİLENDİRME  |  Aylık tutarlar kayıt alanlarındaki değerler üzerinden hesaplanır.\n  Aktif ödeme toplamı yalnızca “Aktif” statüsündeki kayıtları kapsar. Bu rapor kurum içi kullanım içindir.',
    { fill: COLORS.pale, color: COLORS.muted, size: 10, wrap: true },
  );
  ws.getRow(30).height = 29;
  ws.getRow(31).height = 23;
  ws.pageSetup.printArea = 'A1:L31';
  return ws;
}

function getMetrics(rows) {
  const activeRows = rows.filter((row) => row?.status === 'Aktif');
  return {
    total: rows.length,
    active: activeRows.length,
    activeAmount: activeRows.reduce((acc, row) => acc + moneyValue(row?.amount), 0),
    allAmount: rows.reduce((acc, row) => acc + moneyValue(row?.amount), 0),
  };
}

function filterLabel(kind, filters = {}) {
  const parts = [];
  if (filters.searchTerm?.trim()) parts.push(`Arama: ${filters.searchTerm.trim()}`);
  if (filters.statusFilter) parts.push(`Durum: ${filters.statusFilter}`);
  if (kind === 'student' && filters.schoolFilter) parts.push(`Okul: ${filters.schoolFilter}`);
  if (filters.dateRange?.[0] || filters.dateRange?.[1]) {
    parts.push(`Tarih: ${dateLabel(filters.dateRange?.[0])} - ${dateLabel(filters.dateRange?.[1])}`);
  }
  return parts.length ? parts.map(textValue).join('  |  ') : 'Tüm kayıtlar';
}

/**
 * @param {{kind:'student'|'family', rows: object[], filters?: object}} options
 */
export async function exportFinancialSupportExcel({ kind, rows, filters = {} }) {
  const cfg = CONFIG[kind];
  if (!cfg) throw new Error('Geçersiz rapor türü.');
  const safeRows = Array.isArray(rows) ? rows : [];
  const now = new Date();
  const generatedAt = now.toLocaleString('tr-TR');
  const dateStamp = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  const filterDescription = filterLabel(kind, filters);

  const workbook = new ExcelJS.Workbook();
  workbook.creator = 'SMS Grup Portal';
  workbook.lastModifiedBy = 'SMS Grup Portal';
  workbook.created = now;
  workbook.modified = now;
  workbook.subject = cfg.title;
  workbook.title = cfg.title;
  workbook.company = 'SMS Grup';

  const metrics = getMetrics(safeRows);
  summarySheet(workbook, cfg, kind, safeRows, metrics, filterDescription, generatedAt);
  dataSheet(workbook, cfg, kind, safeRows, metrics, filterDescription, generatedAt);
  workbook.views = [{ activeTab: 0 }];

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${cfg.filename}_${dateStamp}.xlsx`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => window.URL.revokeObjectURL(url), 1500);
}
